import React, { useContext, useEffect, useState } from 'react';
import { ThemeContext } from 'styled-components';
import {
  Headline3,
  Body1,
  Icon,
  Button,
  getAppTitle,
  getTextThemeProvider,
  getCurrentTheme,
  getHelpCenterLinks,
} from 'venn-ui-kit';
import { assertNotNil, useHasFF } from 'venn-utils';
import { getHomeContent } from 'venn-api';
import type { WhatsNewContent } from 'venn-api';

const getLink = (url?: string) => {
  if (!url) {
    return '';
  }

  const httpPatern = /^https?:\/{2}\w/i;
  const matches = httpPatern.test(url);

  if (matches) {
    return url;
  }

  return `//${url}`;
};

const SingleSlideHomepage = () => {
  const { Images } = useContext(ThemeContext);
  const [whatsNewContent, setWhatsNewContent] = useState<WhatsNewContent>();
  const hasVennDailyPrivateAssetReturnsMarketingFF = useHasFF('venn_daily_private_asset_returns_marketing_ff');

  useEffect(() => {
    const fetchWhatsNewContent = async () => {
      try {
        const { content } = await getHomeContent();
        const { whatsNewContent } = content;

        if (whatsNewContent.text || whatsNewContent.title) {
          setWhatsNewContent(whatsNewContent);
        }
      } catch (e) {
        setWhatsNewContent(undefined);
      }
    };
    fetchWhatsNewContent();
  }, []);

  return (
    <div className="flex w-full min-w-[880px]">
      <div className="flex max-w-[1440px] w-full">
        <div className="flex-1 min-w-[552px] max-w-[864px] pt-[24px] px-[30px]">
          <div className="text-venn-dark-blue font-bold text-[14px] uppercase tracking-normal leading-normal font-roboto">
            new on {getAppTitle()}
          </div>
          <div className="mt-[12px] mb-[18px]">
            <div className="text-venn-grey-100 font-bold font-roboto text-[40px] leading-[59.8px]">
              Historical Portfolio Allocations on {getAppTitle()}
              {hasVennDailyPrivateAssetReturnsMarketingFF ? null : (
                <span className="ml-[6px] -mt-[3px] bg-venn-highlight-dark text-white px-[6px] py-[6px] rounded-[4px] h-[23px] inline-flex items-center justify-center uppercase font-bold font-roboto text-[12px] leading-[13.8px] align-top">
                  NEW
                </span>
              )}
            </div>
          </div>
          <div className="text-venn-black font-normal leading-normal font-roboto text-[20px]">
            Analyze the evolution of your portfolio, historical decisions, and the impact of those decisions on
            portfolio performance in Analysis, Studio and Report Lab.
          </div>
          <button
            type="button"
            onClick={() =>
              window.open(getHelpCenterLinks().HISTORICAL_PORTFOLIOS_HELP_HREF, '_blank', 'noopener,noreferrer')
            }
            className="mt-[18px] flex w-[240px] h-[40px] min-w-[80px] px-[9px] py-[10px] justify-center items-center gap-[3px] rounded bg-[#0072AF] text-white"
          >
            Learn More
          </button>
          <div className="mt-[18px]">
            <img
              src={assertNotNil(Images.historicalAllocations)}
              alt="Historical Portfolio Allocations demo"
              className="w-full h-auto block p-0 max-w-[753px] max-h-[488px] -ml-[6px]"
            />
          </div>
          {whatsNewContent ? (
            <div className="mt-[24px] p-[18px] border-solid border-0 border-t  border-venn-grey-30">
              <div className="flex items-start">
                <div className="flex-1">
                  <Headline3 className="mb-[9px] text-black font-roboto text-[20px]">
                    <Icon type="bookmark" className="text-black text-[20px]" /> {whatsNewContent.title}
                  </Headline3>
                  <Body1 className="text-venn-black font-roboto">{whatsNewContent.text}</Body1>
                  {whatsNewContent?.url && (
                    <Button
                      onClick={() => window.open(getLink(whatsNewContent?.url), '_blank', 'noopener,noreferrer')}
                      refreshedStyling
                      noMargin
                      className="mt-[9px]"
                      style={{ borderRadius: '4px' }}
                    >
                      Read More
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            getCurrentTheme() === 'green' && (
              <div className="mt-[24px] p-[18px] border-solid border-0 border-t  border-venn-grey-30">
                <div className="flex items-start">
                  <div className="flex-1">
                    <Headline3 className="mb-[9px] text-black font-roboto text-[20px]">
                      <Icon type="bookmark" className="text-black text-[20px] mr-[10px]" />
                      Venn is now Solovis Risk Pro
                    </Headline3>
                    <Body1 className="text-venn-black font-roboto">
                      Nothing else has changed — same login, same features.
                    </Body1>
                  </div>
                </div>
              </div>
            )
          )}
        </div>
        <div className="w-[576px] border-l border-venn-grey-30 bg-venn-grey-10">
          <div className="pt-[24px] px-[30px] w-full">
            <div className="text-venn-dark-blue font-bold text-[14px] uppercase tracking-normal leading-normal font-roboto">
              Additional Solutions*
            </div>
            <div className="mt-[18px] pb-[12px] border-solid border-0 border-b border-venn-grey-30">
              <div className="mt-[12px] text-venn-black font-roboto text-[20px] font-bold leading-normal">
                Private Asset Lab
              </div>
              <div className="mt-[6px] text-venn-grey-80 font-roboto text-[14px] font-normal leading-normal">
                Extensive data library and total portfolio analytics inclusive of private assets. Leverage a broad
                database of private asset investment data, key workflows, and analytics such as cash flow pacing models,
                public market equivalents, asset growth simulations, and more.
              </div>
              {hasVennDailyPrivateAssetReturnsMarketingFF ? (
                <>
                  <div className="mt-[6px] text-venn-grey-80 font-roboto text-[14px] font-normal leading-normal">
                    <span className="mr-[3px] inline-block rounded-[4px] bg-venn-highlight-dark px-[6px] pt-[1px] text-[12px] font-bold uppercase text-white">
                      NEW
                    </span>
                    <span className="font-bold">{getTextThemeProvider().vdparName}</span> transform quarterly private
                    fund cash flows into daily return series by modeling their relationship to public markets.
                  </div>
                  <img
                    src={assertNotNil(Images.privateAssetLabCarouselVdpar)}
                    alt="Private Asset Lab Carousel"
                    className="mt-[6px] h-auto w-full border border-solid border-venn-grey-30"
                  />
                </>
              ) : (
                <img
                  src={assertNotNil(Images.privateAssetLabCarousel)}
                  alt="Private Asset Lab Carousel"
                  className="mt-[6px] h-auto w-full"
                />
              )}
              <Button
                onClick={() => {
                  window.location.href = `mailto:${getTextThemeProvider().salesEmail}?subject=Private Asset Lab`;
                }}
                refreshedStyling
                noMargin
                className="mt-[9px]"
                style={{ borderRadius: '4px' }}
              >
                Learn More
              </Button>
            </div>
            <div className="mt-[18px] pb-[12px] border-solid border-0 border-b border-venn-grey-30">
              <div className="mt-[12px] text-venn-black font-roboto text-[20px] font-bold leading-normal">
                Report Lab
              </div>
              <div className="mt-[6px] text-venn-grey-80 font-roboto text-[14px] font-normal leading-normal">
                Customizable, Flexible, and Archivable: Quickly construct reports and proposals, craft your narrative,
                and present with confidence.
              </div>
              <img
                src={assertNotNil(Images.reportLabAsset)}
                alt="Report Lab Asset"
                className="mt-[6px] h-auto w-full border border-solid border-venn-grey-30"
              />
              <Button
                onClick={() => {
                  window.location.href = `mailto:${getTextThemeProvider().salesEmail}?subject=Report Lab`;
                }}
                refreshedStyling
                noMargin
                className="mt-[9px]"
                style={{ borderRadius: '4px' }}
              >
                Learn More
              </Button>
            </div>
            <div className="mt-[18px] pb-[12px] border-solid border-0 border-b border-venn-grey-30">
              <div className="text-venn-black font-roboto text-[20px] font-bold leading-normal">
                API and Integrations
              </div>
              <div className="mt-[6px] text-venn-grey-80 font-roboto text-[14px] font-normal leading-normal">
                Power your team and client experiences by integrating {getAppTitle()} data and analytics into your
                solutions.
              </div>
              <Button
                onClick={() => {
                  window.location.href = `mailto:${getTextThemeProvider().salesEmail}?subject=API and Integrations`;
                }}
                refreshedStyling
                noMargin
                className="mt-[9px]"
                style={{ borderRadius: '4px' }}
              >
                Learn More
              </Button>
            </div>
            <div className="mt-[6px] mb-[6px] text-venn-grey-80 font-roboto text-[11px] font-normal leading-normal">
              *Some features subject to additional fees.
            </div>
          </div>
        </div>
      </div>
      <div className="flex-1 bg-venn-grey-10">{/* Gray background extension for viewports larger than 1440px */}</div>
    </div>
  );
};

export default SingleSlideHomepage;
